/**
 * Partner Onboarding — "Join & Align" Flow
 *
 * 6 steps: Welcome → Privacy Style → Review Rhythm → Review Goals → Review Plan → Complete
 * Includes "Pause & talk" moments at Goals, Rhythm, and Plan steps.
 * Never wipes existing data. Always allows inline adjustments.
 */
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { useRhythm } from "../components/rhythm/RhythmProvider";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { toast } from "sonner";
import {
  ArrowRight,
  ArrowLeft,
  Loader2,
  Users,
  Shield,
  Calendar,
  Target,
  Wallet,
  Check,
  Heart,
  MessageCircle,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RHYTHM_LABELS = {
  weekly: "Weekly",
  fortnightly: "Fortnightly",
  monthly: "Monthly",
  per_pay_cycle: "Per pay cycle",
};

const RHYTHM_OPTIONS = [
  { value: "weekly", label: "Weekly", desc: "Every 7 days" },
  { value: "fortnightly", label: "Fortnightly", desc: "Every 14 days" },
  { value: "monthly", label: "Monthly", desc: "1st to end of month" },
  { value: "per_pay_cycle", label: "Per pay cycle", desc: "Based on when you get paid" },
];

const DAY_OPTIONS = [
  { value: "1", label: "Monday" },
  { value: "2", label: "Tuesday" },
  { value: "3", label: "Wednesday" },
  { value: "4", label: "Thursday" },
  { value: "5", label: "Friday" },
];

// ============== Pause & Talk Prompt ==============
function PauseTalkPrompt({ children }) {
  return (
    <div className="flex items-start gap-3 bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800/30 rounded-xl p-4 my-4" data-testid="pause-talk-prompt">
      <MessageCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
      <div className="text-sm text-amber-800 dark:text-amber-300">{children}</div>
    </div>
  );
}

// ============== Step 1: Welcome ==============
function WelcomeStep({ onNext }) {
  return (
    <div className="flex flex-col items-center justify-center min-h-[50vh] text-center" data-testid="partner-step-welcome">
      <div className="w-20 h-20 rounded-full bg-[#1a4d3e]/10 flex items-center justify-center mb-8">
        <Users className="w-10 h-10 text-[#1a4d3e]" />
      </div>
      <h1 className="font-heading text-3xl font-bold text-foreground mb-3">
        You've been invited to build this together
      </h1>
      <p className="text-muted-foreground max-w-sm mb-10">
        This is a shared system — you'll shape it together so it works for both of you.
      </p>
      <Button
        size="lg"
        onClick={onNext}
        className="bg-[#1a4d3e] hover:bg-[#1a4d3e]/90 px-10"
        data-testid="partner-welcome-continue"
      >
        Continue
        <ArrowRight className="w-4 h-4 ml-2" />
      </Button>
    </div>
  );
}

// ============== Step 2: Privacy Style ==============
const PRIVACY_OPTIONS = [
  {
    value: "fully_shared",
    label: "Fully Shared",
    desc: "Everything is visible to both of you — one shared view of all money.",
    icon: Users,
  },
  {
    value: "shared_personal",
    label: "Shared + Personal",
    desc: "See shared finances together, but keep personal spending private.",
    icon: Shield,
  },
  {
    value: "separate_shared_goals",
    label: "Separate + Shared Goals",
    desc: "Manage money separately, but work toward shared goals together.",
    icon: Target,
  },
  {
    value: "solo",
    label: "Solo Household",
    desc: "One person manages — the other has view access.",
    icon: Wallet,
  },
];

function PrivacyStep({ onNext, onBack }) {
  const [selected, setSelected] = useState("fully_shared");
  const [saving, setSaving] = useState(false);

  const handleContinue = async () => {
    setSaving(true);
    try {
      await fetch(`${API_URL}/api/onboarding/partner-privacy`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ privacy_style: selected }),
      });
      onNext();
    } catch {
      toast.error("Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="partner-step-privacy">
      <div className="text-center space-y-2">
        <div className="w-14 h-14 rounded-full bg-[#1a4d3e]/10 flex items-center justify-center mx-auto mb-4">
          <Shield className="w-7 h-7 text-[#1a4d3e]" />
        </div>
        <h2 className="font-heading text-2xl font-bold text-foreground">
          How would you like to manage money together?
        </h2>
      </div>

      <div className="space-y-2">
        {PRIVACY_OPTIONS.map((opt) => {
          const Icon = opt.icon;
          return (
            <button
              key={opt.value}
              onClick={() => setSelected(opt.value)}
              className={`w-full flex items-start gap-3 p-4 rounded-xl border text-left transition-colors ${
                selected === opt.value
                  ? "border-[#1a4d3e] bg-[#1a4d3e]/5"
                  : "border-border hover:border-muted-foreground/30"
              }`}
              data-testid={`partner-privacy-${opt.value}`}
            >
              <Icon className={`w-5 h-5 mt-0.5 shrink-0 ${selected === opt.value ? "text-[#1a4d3e]" : "text-muted-foreground"}`} />
              <div>
                <p className="text-sm font-medium">{opt.label}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{opt.desc}</p>
              </div>
              {selected === opt.value && <Check className="w-4 h-4 text-[#1a4d3e] ml-auto mt-0.5 shrink-0" />}
            </button>
          );
        })}
      </div>

      <div className="flex justify-between pt-4">
        <Button variant="ghost" onClick={onBack} data-testid="partner-privacy-back">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back
        </Button>
        <Button onClick={handleContinue} disabled={saving} className="bg-[#1a4d3e] hover:bg-[#1a4d3e]/90" data-testid="partner-privacy-continue">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Continue"}
          {!saving && <ArrowRight className="w-4 h-4 ml-2" />}
        </Button>
      </div>
    </div>
  );
}

// ============== Step 3: Review Rhythm ==============
function RhythmReviewStep({ context, onNext, onBack }) {
  const [mode, setMode] = useState("review"); // review | change
  const [newRhythm, setNewRhythm] = useState(context?.rhythm?.rhythm_type || "weekly");
  const [anchorType, setAnchorType] = useState(context?.rhythm?.pay_anchor_type || "date");
  const [anchorValue, setAnchorValue] = useState(context?.rhythm?.pay_anchor_value || "1");
  const [saving, setSaving] = useState(false);

  const currentRhythm = context?.rhythm?.rhythm_type || "weekly";

  const handleKeep = async () => {
    setSaving(true);
    try {
      await fetch(`${API_URL}/api/onboarding/partner-rhythm`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ action: "keep" }),
      });
      onNext();
    } catch {
      toast.error("Could not save");
    } finally {
      setSaving(false);
    }
  };

  const handleChange = async () => {
    setSaving(true);
    try {
      await fetch(`${API_URL}/api/onboarding/partner-rhythm`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          action: "change",
          rhythm_type: newRhythm,
          pay_anchor_type: newRhythm === "per_pay_cycle" ? anchorType : null,
          pay_anchor_value: newRhythm === "per_pay_cycle" ? anchorValue : null,
        }),
      });
      onNext();
    } catch {
      toast.error("Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="partner-step-rhythm">
      <div className="text-center space-y-2">
        <div className="w-14 h-14 rounded-full bg-[#1a4d3e]/10 flex items-center justify-center mx-auto mb-4">
          <Calendar className="w-7 h-7 text-[#1a4d3e]" />
        </div>
        <h2 className="font-heading text-2xl font-bold text-foreground">
          Does this rhythm work for you?
        </h2>
        <p className="text-muted-foreground max-w-sm mx-auto">
          This is how your household will run — you can adjust it together.
        </p>
      </div>

      <PauseTalkPrompt>
        This works best when it reflects both of your lives — take a moment to agree on what fits.
      </PauseTalkPrompt>

      {mode === "review" ? (
        <>
          <div className="bg-[#1a4d3e]/5 rounded-xl p-5 text-center">
            <p className="text-xs text-muted-foreground mb-1">Current rhythm</p>
            <p className="text-lg font-semibold text-[#1a4d3e]" data-testid="partner-current-rhythm">
              {RHYTHM_LABELS[currentRhythm] || "Weekly"}
            </p>
          </div>

          <div className="flex gap-3">
            <Button onClick={handleKeep} disabled={saving} className="flex-1 bg-[#1a4d3e] hover:bg-[#1a4d3e]/90" data-testid="partner-rhythm-keep">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Keep this"}
            </Button>
            <Button variant="outline" onClick={() => setMode("change")} className="flex-1" data-testid="partner-rhythm-change-btn">
              Change it
            </Button>
          </div>
        </>
      ) : (
        <>
          <div className="space-y-2">
            {RHYTHM_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                onClick={() => setNewRhythm(opt.value)}
                className={`w-full flex items-center justify-between p-3 rounded-xl border text-left transition-colors ${
                  newRhythm === opt.value ? "border-[#1a4d3e] bg-[#1a4d3e]/5" : "border-border"
                }`}
                data-testid={`partner-rhythm-opt-${opt.value}`}
              >
                <div>
                  <p className="text-sm font-medium">{opt.label}</p>
                  <p className="text-xs text-muted-foreground">{opt.desc}</p>
                </div>
                {newRhythm === opt.value && <Check className="w-4 h-4 text-[#1a4d3e]" />}
              </button>
            ))}
          </div>

          {newRhythm === "per_pay_cycle" && (
            <div className="border-t border-border pt-4 space-y-3">
              <p className="text-sm font-medium">When does your income usually arrive?</p>
              <div className="flex gap-2">
                <button onClick={() => setAnchorType("date")} className={`flex-1 p-2 rounded-lg border text-sm ${anchorType === "date" ? "border-[#1a4d3e] bg-[#1a4d3e]/5" : "border-border"}`}>Specific date</button>
                <button onClick={() => setAnchorType("day")} className={`flex-1 p-2 rounded-lg border text-sm ${anchorType === "day" ? "border-[#1a4d3e] bg-[#1a4d3e]/5" : "border-border"}`}>Day of week</button>
              </div>
              {anchorType === "date" ? (
                <div className="grid grid-cols-7 gap-1">
                  {Array.from({ length: 28 }, (_, i) => {
                    const v = String(i + 1);
                    const suffix = ["st","nd","rd"][((i+1)%100-20)%10-1] || ["st","nd","rd"][(i+1)%100-1] || "th";
                    return (
                      <button key={v} onClick={() => setAnchorValue(v)} className={`p-1.5 rounded text-xs font-medium ${anchorValue === v ? "bg-[#1a4d3e] text-white" : "hover:bg-muted"}`}>{i+1}{suffix}</button>
                    );
                  })}
                </div>
              ) : (
                <div className="space-y-1">
                  {DAY_OPTIONS.map((d) => (
                    <button key={d.value} onClick={() => setAnchorValue(d.value)} className={`w-full p-2 rounded-lg border text-left text-sm ${anchorValue === d.value ? "border-[#1a4d3e] bg-[#1a4d3e]/5" : "border-border"}`}>{d.label}</button>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="flex gap-3">
            <Button variant="outline" onClick={() => setMode("review")}>Cancel</Button>
            <Button onClick={handleChange} disabled={saving} className="flex-1 bg-[#1a4d3e] hover:bg-[#1a4d3e]/90" data-testid="partner-rhythm-save">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save & continue"}
            </Button>
          </div>
        </>
      )}

      <div className="pt-2">
        <Button variant="ghost" onClick={onBack} data-testid="partner-rhythm-back">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back
        </Button>
      </div>
    </div>
  );
}

// ============== Step 4: Review Goals ==============
function GoalsReviewStep({ context, onNext, onBack }) {
  const goals = context?.goals || [];
  const [saving, setSaving] = useState(false);

  const handleContinue = async () => {
    setSaving(true);
    try {
      await fetch(`${API_URL}/api/onboarding/partner-next`, { method: "POST", credentials: "include" });
      onNext();
    } catch {
      toast.error("Could not continue");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="partner-step-goals">
      <div className="text-center space-y-2">
        <div className="w-14 h-14 rounded-full bg-[#1a4d3e]/10 flex items-center justify-center mx-auto mb-4">
          <Target className="w-7 h-7 text-[#1a4d3e]" />
        </div>
        <h2 className="font-heading text-2xl font-bold text-foreground">
          Do these feel right?
        </h2>
        <p className="text-muted-foreground max-w-sm mx-auto">
          These are what you're working toward together.
        </p>
      </div>

      <PauseTalkPrompt>
        <p className="font-medium mb-1">Take a moment together</p>
        <p>Before you add or change anything, pause here and talk about what matters most right now.</p>
        <p className="mt-1 text-xs opacity-80">You can enter your goals on one account — they'll show up for both of you.</p>
      </PauseTalkPrompt>

      {goals.length > 0 ? (
        <div className="space-y-2">
          {goals.map((goal) => (
            <div key={goal.goal_id} className="flex items-center justify-between bg-card border border-border rounded-xl p-4" data-testid={`partner-goal-${goal.goal_id}`}>
              <div>
                <p className="text-sm font-medium">{goal.title}</p>
                {goal.target_amount && (
                  <p className="text-xs text-muted-foreground">
                    Target: ${goal.target_amount?.toLocaleString()}
                    {goal.current_amount > 0 && ` · ${Math.round((goal.current_amount / goal.target_amount) * 100)}% there`}
                  </p>
                )}
              </div>
              <span className={`text-xs px-2 py-1 rounded-full ${goal.status === "in_progress" ? "bg-[#1a4d3e]/10 text-[#1a4d3e]" : "bg-muted text-muted-foreground"}`}>
                {goal.status === "in_progress" ? "In progress" : goal.status}
              </span>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-6 bg-muted/30 rounded-xl">
          <p className="text-sm text-muted-foreground">No goals set yet — you can add them together from The Plan.</p>
        </div>
      )}

      <div className="flex justify-between pt-4">
        <Button variant="ghost" onClick={onBack} data-testid="partner-goals-back">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back
        </Button>
        <Button onClick={handleContinue} disabled={saving} className="bg-[#1a4d3e] hover:bg-[#1a4d3e]/90" data-testid="partner-goals-continue">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Looks good"}
          {!saving && <ArrowRight className="w-4 h-4 ml-2" />}
        </Button>
      </div>
    </div>
  );
}

// ============== Step 5: Review Plan ==============
function PlanReviewStep({ context, onNext, onBack }) {
  const allocations = context?.allocations || [];
  const [saving, setSaving] = useState(false);

  const buckets = {};
  allocations.forEach((a) => {
    const b = a.bucket || "Other";
    if (!buckets[b]) buckets[b] = [];
    buckets[b].push(a);
  });

  const handleContinue = async () => {
    setSaving(true);
    try {
      await fetch(`${API_URL}/api/onboarding/partner-next`, { method: "POST", credentials: "include" });
      onNext();
    } catch {
      toast.error("Could not continue");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="partner-step-plan">
      <div className="text-center space-y-2">
        <div className="w-14 h-14 rounded-full bg-[#1a4d3e]/10 flex items-center justify-center mx-auto mb-4">
          <Wallet className="w-7 h-7 text-[#1a4d3e]" />
        </div>
        <h2 className="font-heading text-2xl font-bold text-foreground">
          This is how your money is organised
        </h2>
        <p className="text-muted-foreground max-w-sm mx-auto">
          You can adjust anything — this is your shared system.
        </p>
      </div>

      <PauseTalkPrompt>
        This is your shared structure — a quick conversation here makes everything easier later.
      </PauseTalkPrompt>

      {Object.keys(buckets).length > 0 ? (
        <div className="space-y-3">
          {Object.entries(buckets).map(([bucket, items]) => (
            <div key={bucket} className="bg-card border border-border rounded-xl p-4">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">{bucket}</p>
              <div className="space-y-1">
                {items.map((item) => (
                  <div key={item.entry_id} className="flex justify-between text-sm">
                    <span>{item.name}</span>
                    <span className="font-medium tabular-nums">${item.amount?.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-6 bg-muted/30 rounded-xl">
          <p className="text-sm text-muted-foreground">No allocations yet — you can build your plan together from The Plan.</p>
        </div>
      )}

      <div className="flex justify-between pt-4">
        <Button variant="ghost" onClick={onBack} data-testid="partner-plan-back">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back
        </Button>
        <Button onClick={handleContinue} disabled={saving} className="bg-[#1a4d3e] hover:bg-[#1a4d3e]/90" data-testid="partner-plan-continue">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Continue"}
          {!saving && <ArrowRight className="w-4 h-4 ml-2" />}
        </Button>
      </div>
    </div>
  );
}

// ============== Step 6: Alignment Complete ==============
function AlignmentComplete({ onDone }) {
  const [intention, setIntention] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleSave = async () => {
    if (!intention.trim()) return;
    setSaving(true);
    try {
      await fetch(`${API_URL}/api/onboarding/journal-intention`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ content: intention.trim() }),
      });
      setSaved(true);
      setTimeout(() => onDone(), 1200);
    } catch {
      // Continue regardless
      onDone();
    } finally {
      setSaving(false);
    }
  };

  if (saved) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh] text-center" data-testid="partner-step-complete">
        <div className="w-20 h-20 rounded-full bg-[#1a4d3e]/10 flex items-center justify-center mb-8">
          <Heart className="w-10 h-10 text-[#1a4d3e]" />
        </div>
        <h1 className="font-heading text-3xl font-bold text-foreground mb-3">
          You're now on the same page
        </h1>
        <p className="text-muted-foreground max-w-sm mb-4">
          Your intention is saved. Everything starts here.
        </p>
        <Loader2 className="w-5 h-5 animate-spin text-[#1a4d3e]" />
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-[50vh] text-center" data-testid="partner-step-complete">
      <div className="w-20 h-20 rounded-full bg-[#1a4d3e]/10 flex items-center justify-center mb-8">
        <Heart className="w-10 h-10 text-[#1a4d3e]" />
      </div>
      <h1 className="font-heading text-3xl font-bold text-foreground mb-3">
        You're now on the same page
      </h1>
      <p className="text-muted-foreground max-w-sm mb-6">
        This is your shared system — from here, everything becomes clearer and easier to manage together.
      </p>

      {/* Intention Journal */}
      <div className="w-full max-w-sm text-left bg-[#1a4d3e]/5 rounded-xl p-5 border border-[#1a4d3e]/10 mb-6">
        <p className="text-sm font-medium text-foreground mb-1">Set your intention together</p>
        <p className="text-xs text-muted-foreground mb-3">
          Take a moment to write what you both want to feel about money this year.
        </p>
        <input
          type="text"
          value={intention}
          onChange={(e) => setIntention(e.target.value)}
          placeholder="We want to feel..."
          className="w-full px-3 py-2 text-sm rounded-lg border border-border bg-background focus:outline-none focus:ring-1 focus:ring-[#1a4d3e]/30"
          data-testid="partner-intention-input"
        />
        <p className="text-[11px] text-muted-foreground/60 mt-2">
          This doesn't need to be perfect — just honest.
        </p>
      </div>

      <div className="flex gap-3 w-full max-w-sm">
        <Button
          variant="ghost"
          onClick={onDone}
          className="flex-1"
          data-testid="partner-skip-intention"
        >
          Skip
        </Button>
        <Button
          onClick={intention.trim() ? handleSave : onDone}
          disabled={saving}
          className="flex-1 bg-[#1a4d3e] hover:bg-[#1a4d3e]/90"
          data-testid="partner-save-intention"
        >
          {saving ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : intention.trim() ? (
            <>Save and continue</>
          ) : (
            <>Enter dashboard <ArrowRight className="w-4 h-4 ml-1" /></>
          )}
        </Button>
      </div>
    </div>
  );
}

// ============== Main ==============
const STEPS = ["welcome", "privacy", "rhythm", "goals", "plan", "complete"];

export default function PartnerOnboardingPage() {
  const { checkAuth } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [currentState, setCurrentState] = useState("welcome");
  const [context, setContext] = useState(null);

  const fetchState = useCallback(async () => {
    try {
      const [stateRes, ctxRes] = await Promise.all([
        fetch(`${API_URL}/api/onboarding/partner-state`, { credentials: "include" }),
        fetch(`${API_URL}/api/onboarding/partner-context`, { credentials: "include" }),
      ]);
      if (stateRes.ok) {
        const data = await stateRes.json();
        if (data.current_state === "complete") {
          navigate("/dashboard");
          return;
        }
        setCurrentState(data.current_state);
      }
      if (ctxRes.ok) {
        setContext(await ctxRes.json());
      }
    } catch {
      toast.error("Could not load onboarding");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => { fetchState(); }, [fetchState]);

  const advanceBackend = async () => {
    try {
      const res = await fetch(`${API_URL}/api/onboarding/partner-next`, { method: "POST", credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setCurrentState(data.current_state);
      }
    } catch {
      toast.error("Could not advance");
    }
  };

  const goBack = async () => {
    try {
      const res = await fetch(`${API_URL}/api/onboarding/partner-back`, { method: "POST", credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setCurrentState(data.current_state);
      }
    } catch {
      toast.error("Could not go back");
    }
  };

  // Goals & Plan steps call partner-next themselves
  const handleGoalsNext = () => setCurrentState("plan");
  const handlePlanNext = () => setCurrentState("complete");

  const handleDone = async () => {
    try {
      await fetch(`${API_URL}/api/onboarding/partner-complete`, { method: "POST", credentials: "include" });
    } catch { /* ok */ }
    await checkAuth();
    navigate("/dashboard");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#1a4d3e]" />
      </div>
    );
  }

  const stepIndex = STEPS.indexOf(currentState);
  const progressPercent = Math.min(((stepIndex + 1) / STEPS.length) * 100, 100);
  const stepLabel = currentState === "complete" ? "You're aligned" : `Step ${stepIndex + 1} of ${STEPS.length - 1}`;

  return (
    <div className="min-h-screen bg-background" data-testid="partner-onboarding-flow">
      <div className="container max-w-lg mx-auto px-4 py-8">
        <div className="mb-8">
          <div className="flex justify-between text-xs text-muted-foreground mb-2">
            <span>{stepLabel}</span>
          </div>
          <Progress value={progressPercent} className="h-1.5" data-testid="partner-onboarding-progress" />
        </div>

        <div className="bg-card rounded-2xl border border-border p-6 md:p-8 shadow-sm">
          {currentState === "welcome" && <WelcomeStep onNext={advanceBackend} />}
          {currentState === "privacy" && <PrivacyStep onNext={advanceBackend} onBack={goBack} />}
          {currentState === "rhythm" && <RhythmReviewStep context={context} onNext={advanceBackend} onBack={goBack} />}
          {currentState === "goals" && <GoalsReviewStep context={context} onNext={handleGoalsNext} onBack={goBack} />}
          {currentState === "plan" && <PlanReviewStep context={context} onNext={handlePlanNext} onBack={goBack} />}
          {currentState === "complete" && <AlignmentComplete onDone={handleDone} />}
        </div>

        <p className="text-center text-xs text-muted-foreground/60 mt-6">
          You can always adjust these later — this is your shared system.
        </p>
      </div>
    </div>
  );
}
